import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { WA_PHYSICS } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * J.D. ("CakeBot", Thor's Hammer): an electric box-bot. Both shots are lobbed
 * electric orbs that explode on contact like a bazooka, but bend the knockback
 * through the explosion push table:
 *
 *  - Shot 2 / T2 (Magnet Sphere): PULLS every worm in the blast toward the
 *    impact point (negative push multiplier) - the signature bunging shot.
 *  - SS (Anti-Magnet): the opposite, a hard outward SHOVE plus more damage and
 *    a bigger blast.
 *
 * The pull/push lives in EXPLOSION_PUSH_BY_TYPE (entityFactory) keyed on the
 * projectile type; here we only spawn the orb. Both ride the standard
 * projectile sync (plain `Projectile`s).
 */

function launchOrb(
  ctx: WeaponContext,
  type: 'jdT2' | 'jdSS',
  explosionRadius: number,
  maxDamage: number,
): WeaponResult {
  const { worm, angle, power, world } = ctx;
  const speed = (power / 100) * WA_PHYSICS.JD.LAUNCH_SPEED;
  const rad = (angle * Math.PI) / 180;
  const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
  const spawn = computeSafeSpawnPosition(worm, aimRad, 22, world);

  return {
    success: true,
    entities: [
      new Projectile({
        x: spawn.x,
        y: spawn.y,
        vx: Math.cos(aimRad) * speed,
        vy: -Math.sin(aimRad) * speed,
        type,
        timer: -1,
        bounceH: 0,
        bounceV: 0,
        windAffected: true,
        explosionRadius,
        maxDamage,
        ownerId: worm.id,
      }),
    ],
  };
}

/** J.D. Shot 2 - Magnet Sphere: explodes on contact and pulls worms inward. */
export const WeaponJdT2: WeaponDefinition = {
  id: 'jdT2',
  name: 'Magnet Sphere',
  icon: 'jdT2.png',
  category: 'projectile',
  maxAmmo: -1,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    return launchOrb(ctx, 'jdT2', WA_PHYSICS.JD.T2_EXPLOSION_RADIUS, WA_PHYSICS.JD.T2_MAX_DAMAGE);
  },
};

/** J.D. Super Shot - Anti-Magnet: bigger blast, more damage, hard outward shove. */
export const WeaponJdSS: WeaponDefinition = {
  id: 'jdSS',
  name: 'Anti-Magnet',
  icon: 'jdSS.png',
  category: 'projectile',
  maxAmmo: 1,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    return launchOrb(ctx, 'jdSS', WA_PHYSICS.JD.SS_EXPLOSION_RADIUS, WA_PHYSICS.JD.SS_MAX_DAMAGE);
  },
};
